import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { toast } from "react-toastify";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import FunnelManageDialog from "../../components/FunnelManageDialog";
import { AuthContext } from "../../context/Auth/AuthContext";
import { listTags, createTag, updateTag, deleteTag } from "../../services/tags";
import {
  listQuickAnswerCategories,
  createQuickAnswerCategory,
  updateQuickAnswerCategory,
  deleteQuickAnswerCategory,
} from "../../services/quickAnswerCategories";

const COLORS = [
  "#e53935", "#d81b60", "#8e24aa", "#5e35b1",
  "#1e88e5", "#00897b", "#43a047", "#fb8c00",
  "#f4511e", "#546e7a", "#6d4c41", "#039be5"
];

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(8, 8, 3),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
  },
  settingOption: {
    marginLeft: "auto",
  },
  margin: {
    margin: theme.spacing(1),
  },
  tagSection: {
    padding: theme.spacing(2),
    marginBottom: 12,
  },
  tagHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  tagList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  tagItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  colorPalette: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  colorSwatch: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
  },
  colorSwatchSelected: {
    border: "2px solid #000",
  },
}));

const Settings = () => {
  const classes = useStyles();
  const [settings, setSettings] = useState([]);
  const { user } = useContext(AuthContext);
  const [funnelDialogOpen, setFunnelDialogOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(COLORS[0]);

  const [quickAnswerCategories, setQuickAnswerCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryColor, setCategoryColor] = useState(COLORS[0]);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
    fetchTags();
    fetchQuickAnswerCategories();
  }, []);

  const fetchTags = async () => {
    try {
      const data = await listTags();
      setTags(data);
    } catch (err) {
      toastError(err);
    }
  };

  const fetchQuickAnswerCategories = async () => {
    try {
      const data = await listQuickAnswerCategories();
      setQuickAnswerCategories(data);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const socket = openSocket();
    socket.on("settings", data => {
      if (data.action === "update") {
        setSettings(prevState => {
          const aux = [...prevState];
          const settingIndex = aux.findIndex(s => s.key === data.setting.key);
          aux[settingIndex].value = data.setting.value;
          return aux;
        });
      }
    });
    return () => { socket.disconnect(); };
  }, []);

  const handleChangeSetting = async e => {
    const selectedValue = e.target.value;
    const settingKey = e.target.name;
    try {
      await api.put(`/settings/${settingKey}`, { value: selectedValue });
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const getSettingValue = key => {
    const { value } = settings.find(s => s.key === key);
    return value;
  };

  const handleOpenDialog = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setTagName(tag.name);
      setTagColor(tag.color);
    } else {
      setEditingTag(null);
      setTagName("");
      setTagColor(COLORS[0]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTag(null);
    setTagName("");
    setTagColor(COLORS[0]);
  };

  const handleSaveTag = async () => {
    if (!tagName.trim()) return;
    try {
      if (editingTag) {
        await updateTag(editingTag.id, { name: tagName, color: tagColor });
        toast.success("Etiqueta actualizada");
      } else {
        await createTag({ name: tagName, color: tagColor });
        toast.success("Etiqueta creada");
      }
      fetchTags();
      handleCloseDialog();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteTag = async (id) => {
    try {
      await deleteTag(id);
      toast.success("Etiqueta eliminada");
      fetchTags();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategoryColor(category.color);
    } else {
      setEditingCategory(null);
      setCategoryName("");
      setCategoryColor(COLORS[0]);
    }
    setCategoryDialogOpen(true);
  };

  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setEditingCategory(null);
    setCategoryName("");
    setCategoryColor(COLORS[0]);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;
    try {
      if (editingCategory) {
        await updateQuickAnswerCategory(editingCategory.id, {
          name: categoryName,
          color: categoryColor,
        });
        toast.success("Categoría actualizada");
      } else {
        await createQuickAnswerCategory({
          name: categoryName,
          color: categoryColor,
        });
        toast.success("Categoría creada");
      }
      fetchQuickAnswerCategories();
      handleCloseCategoryDialog();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await deleteQuickAnswerCategory(id);
      toast.success("Categoría eliminada");
      fetchQuickAnswerCategories();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="sm">
        <Typography variant="body2" gutterBottom>
          {i18n.t("settings.title")}
        </Typography>

        <Paper className={classes.paper}>
          <Typography variant="body1">
            {i18n.t("settings.settings.userCreation.name")}
          </Typography>
          <Select
            margin="dense"
            variant="outlined"
            native
            id="userCreation-setting"
            name="userCreation"
            value={settings && settings.length > 0 && getSettingValue("userCreation")}
            className={classes.settingOption}
            onChange={handleChangeSetting}
          >
            <option value="enabled">{i18n.t("settings.settings.userCreation.options.enabled")}</option>
            <option value="disabled">{i18n.t("settings.settings.userCreation.options.disabled")}</option>
          </Select>
        </Paper>

        <Paper className={classes.paper}>
          <TextField
            id="api-token-setting"
            readonly
            label="Token Api"
            margin="dense"
            variant="outlined"
            fullWidth
            value={settings && settings.length > 0 && getSettingValue("userApiToken")}
          />
        </Paper>

        {user?.profile === "admin" && (
          <Paper className={classes.paper}>
            <div>
              <Typography variant="body1">Embudos</Typography>
              <Typography variant="caption" color="textSecondary">
                Crea embudos y asigna sus conexiones y usuarios.
              </Typography>
            </div>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.settingOption}
              onClick={() => setFunnelDialogOpen(true)}
            >
              Gestionar embudos
            </Button>
          </Paper>
        )}

        <Paper className={classes.tagSection}>
          <div className={classes.tagHeader}>
            <Typography variant="body1">Etiquetas</Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nueva etiqueta
            </Button>
          </div>
          <div className={classes.tagList}>
            {tags.map(tag => (
              <div key={tag.id} className={classes.tagItem}>
                <Chip
                  label={tag.name}
                  size="small"
                  style={{ backgroundColor: tag.color, color: "#fff" }}
                />
                <IconButton size="small" onClick={() => handleOpenDialog(tag)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteTag(tag.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {tags.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No hay etiquetas creadas aún.
              </Typography>
            )}
          </div>
        </Paper>

        <Paper className={classes.tagSection}>
          <div className={classes.tagHeader}>
            <Typography variant="body1">Categorías de Respuestas rápidas</Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenCategoryDialog()}
            >
              Nueva categoría
            </Button>
          </div>
          <div className={classes.tagList}>
            {quickAnswerCategories.map(category => (
              <div key={category.id} className={classes.tagItem}>
                <Chip
                  label={category.name}
                  size="small"
                  style={{ backgroundColor: category.color, color: "#fff" }}
                />
                <IconButton size="small" onClick={() => handleOpenCategoryDialog(category)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteCategory(category.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {quickAnswerCategories.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No hay categorías creadas aún.
              </Typography>
            )}
          </div>
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>{editingTag ? "Editar etiqueta" : "Nueva etiqueta"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Nombre"
            fullWidth
            margin="dense"
            value={tagName}
            onChange={e => setTagName(e.target.value)}
          />
          <Typography variant="caption" style={{ marginTop: 12, display: "block" }}>
            Color:
          </Typography>
          <div className={classes.colorPalette}>
            {COLORS.map(color => (
              <div
                key={color}
                className={`${classes.colorSwatch} ${tagColor === color ? classes.colorSwatchSelected : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => setTagColor(color)}
              />
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Typography variant="caption">Vista previa:</Typography>
            <br />
            <Chip
              label={tagName || "Etiqueta"}
              size="small"
              style={{ backgroundColor: tagColor, color: "#fff", marginTop: 4 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="default">Cancelar</Button>
          <Button onClick={handleSaveTag} color="primary" variant="contained">
            {editingTag ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={categoryDialogOpen} onClose={handleCloseCategoryDialog}>
        <DialogTitle>
          {editingCategory ? "Editar categoría" : "Nueva categoría"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Nombre"
            fullWidth
            margin="dense"
            value={categoryName}
            onChange={e => setCategoryName(e.target.value)}
          />
          <Typography variant="caption" style={{ marginTop: 12, display: "block" }}>
            Color:
          </Typography>
          <div className={classes.colorPalette}>
            {COLORS.map(color => (
              <div
                key={color}
                className={`${classes.colorSwatch} ${categoryColor === color ? classes.colorSwatchSelected : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => setCategoryColor(color)}
              />
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Typography variant="caption">Vista previa:</Typography>
            <br />
            <Chip
              label={categoryName || "Categoría"}
              size="small"
              style={{ backgroundColor: categoryColor, color: "#fff", marginTop: 4 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCategoryDialog} color="default">Cancelar</Button>
          <Button onClick={handleSaveCategory} color="primary" variant="contained">
            {editingCategory ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
      {user?.profile === "admin" && (
        <FunnelManageDialog
          open={funnelDialogOpen}
          onClose={() => setFunnelDialogOpen(false)}
          onChanged={() => {}}
        />
      )}
    </div>
  );
};

export default Settings;